import React, { FC, useContext, useEffect, useMemo } from 'react';

import { useMutation } from '@apollo/client';
import {
  Button,
  H2,
  Input,
  Link,
  Modal,
  Paragraph,
  PasswordInput,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import {
  CONNECT_PAYMENT_SERVICES,
  DISCONNECT_PAYMENT_SERVICE,
  EConnectPaymentService,
  GET_SETTINGS_VALUE,
  IConnectPaymentServiceRequest,
  IDisconnectPaymentServiceRequest,
  SettingsContext,
} from 'settings';

import { IBaseModalProps } from '../interfaces';
import { IStripeFormData } from './interfaces';
import s from './StripeModal.module.scss';

export const StripeModal: FC<IBaseModalProps> = ({ onClose }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { stripeEnabled, stripeSecretKey, stripePublishableKey },
    },
  } = useContext(SettingsContext);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    watch,
  } = useForm<IStripeFormData>({
    mode: 'onChange',
    defaultValues: {
      publishableKey: '',
      secretKey: '',
    },
  });

  const [connectPaymentService, { loading }] = useMutation<
    boolean,
    IConnectPaymentServiceRequest
  >(CONNECT_PAYMENT_SERVICES, {
    refetchQueries: [GET_SETTINGS_VALUE],
    awaitRefetchQueries: true,
  });

  const [
    disconnectPaymentService,
    { loading: disconnectPaymentServiceLoading },
  ] = useMutation<boolean, IDisconnectPaymentServiceRequest>(
    DISCONNECT_PAYMENT_SERVICE,
    {
      refetchQueries: [GET_SETTINGS_VALUE],
      awaitRefetchQueries: true,
    }
  );

  useEffect(() => {
    reset({
      secretKey: stripeSecretKey || '',
      publishableKey: stripePublishableKey || '',
    });
  }, [stripePublishableKey, stripePublishableKey]);

  const disconnectPaymentServiceHandler = (): void => {
    disconnectPaymentService({
      variables: { service: EConnectPaymentService.STRIPE },
    })
      .then(() =>
        notification.info({
          type: 'warning',
          t,
          title: t('settings.integration.disabled.title'),
          message: t('settings.integration.disabled.message'),
        })
      )
      .then(() => onClose())
      .catch((err) => console.error(err?.message));
  };

  const secretKey = watch('secretKey');
  const publishableKey = watch('publishableKey');

  const isDisabled = useMemo(
    () => !isDirty || secretKey.length < 24 || publishableKey.length < 24,
    [isDirty, secretKey, publishableKey]
  );

  return (
    <Modal onClose={onClose} className={s.container}>
      <div className={s.header}>
        <H2>{t('settings.integration.stripe.modal.title')}</H2>
        <Paragraph>
          {t('settings.integration.stripe.modal.description')}
        </Paragraph>
      </div>
      <div className={s.body}>
        <form
          className={s.form}
          onSubmit={handleSubmit(({ secretKey, publishableKey }) => {
            if (!secretKey || !publishableKey) return;

            connectPaymentService({
              variables: {
                service: EConnectPaymentService.STRIPE,
                input: {
                  stripe_account: {
                    secret_key: secretKey,
                    publishable_key: publishableKey,
                  },
                },
              },
            })
              .then(() =>
                notification.info({
                  type: 'success',
                  t,
                  title: t('settings.integration.stripe.modal.title'),
                  message: t('integration.online'),
                })
              )
              .then(() => onClose())
              .catch(() => {
                notification.info({
                  type: 'error',
                  t,
                  title: t('errors.error'),
                  message: t('settings.integration.error.message'),
                });
              });
          })}
        >
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title="Public key"
                variant="primary"
                error={error?.message}
              />
            )}
            name="publishableKey"
            control={control}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 24,
                message: t('errors.minLength', 24),
              },
            }}
          />
          <Controller
            render={({ field, fieldState: { error } }) => (
              <PasswordInput
                {...field}
                title="Secret key"
                error={error?.message}
                autoComplete="off"
              />
            )}
            name="secretKey"
            control={control}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 24,
                message: t('errors.minLength', 24),
              },
            }}
          />
          <Link
            title={t('settings.integration.stripe.modal.api')}
            link="https://stripe.com/docs/api"
          />
          <div className={s.buttons}>
            <Button
              fullWidth
              disabled={isDisabled || disconnectPaymentServiceLoading}
              type="submit"
              isLoading={loading}
            >
              {t('save')}
            </Button>
            <Button
              fullWidth
              variant="secondary"
              color="red"
              disabled={!stripeEnabled || loading}
              isLoading={disconnectPaymentServiceLoading}
              onClick={disconnectPaymentServiceHandler}
            >
              {t('integration.offline')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
